import ScaledCanvasContext from "../scaled-canvas-context.js";
import * as csf from "../canvas-state-functions.js";
import { powerUpDefinitions } from "core/src/core/constants.js";
import { drawWallHackWalls, getWallHackVariant } from "../wallhack-designs";
import { boardSkinById, getEquippedBoardSkin } from "../board-skins";
import { makeGeo, makeSkinPainter } from "../board-skin-draw";

var MAP_BACKGROUND_COLOR = "#0d1320";
var MAP_BORDER_COLOR = "#2b3b56";
var WALLHACK_SELF_COLOR = "#22c55e"; // green (affects self)
var WALLHACK_ALL_COLOR = "#4f7bff";  // blue (affects all)

// wall_hack effect length, for the countdown-style wall designs. The effect object only
// carries its grab time, so look the duration up by name and drain off that.
var WALLHACK_DURATION = 0;
for (var _k in powerUpDefinitions) {
    var _d = powerUpDefinitions[_k];
    if (_d && _d.name === "wall_hack" && _d.effectDuration) WALLHACK_DURATION = _d.effectDuration;
}

// `localPlayerIds` (optional): the worms THIS client controls. A GREEN wall hack (yours
// alone) shows on the walls only when one of these has it (online: your own; offline it's
// undefined, so any worm counts, which suits a shared-screen hotseat). A BLUE one
// (affects all) opens the walls for every worm on the field, so it shows whoever grabbed
// it, and whether or not your own worm is still alive to watch.
export default function MapRenderer({ gameState, canvas, borderCanvas, scale=1, centerText, localPlayerIds }) {

    var context = canvas.getContext("2d");
    var scaledContext = ScaledCanvasContext(context, scale);
    var borderContext = borderCanvas.getContext("2d");
    var scaledBorderContext = ScaledCanvasContext(borderContext, scale);

    // Board skin (shop cosmetic, viewer-local): resolved once per renderer
    // construction (a new round / canvas remount picks up an equip change).
    var boardSkin = boardSkinById(getEquippedBoardSkin());
    var backgroundColor = boardSkin ? boardSkin.bg : MAP_BACKGROUND_COLOR;
    // Drawn skin (floor + wall passes) in the outer-box frame; see board-skin-draw.
    var skinPainter = null;
    var skinOrigin = { x: 0, y: 0 };
    if (boardSkin) {
        var sh = gameState.map.shape, bwPx = gameState.map.borderWidth * scale;
        if (sh.type === "circle") {
            skinOrigin = { x: (sh.centerX - sh.radius) * scale - bwPx, y: (sh.centerY - sh.radius) * scale - bwPx };
            skinPainter = makeSkinPainter(boardSkin.id, makeGeo("circle", sh.radius * 2 * scale + 2 * bwPx, bwPx, scale));
        } else if (sh.type === "rectangle" || sh.type === "stadium") {
            // any rectangle ("square" in the skin code), or a stadium (a pill)
            skinOrigin = { x: sh.x * scale - bwPx, y: sh.y * scale - bwPx };
            skinPainter = makeSkinPainter(boardSkin.id, makeGeo(sh.type === "stadium" ? "stadium" : "square",
                sh.width * scale + 2 * bwPx, bwPx, scale, sh.height * scale + 2 * bwPx));
        }
    }

    // A stadium's outline (a pill) at radius r in world units, as a path on the
    // given raw context (pixels).
    function stadiumPath(ctx, shape, r) {
        var l = (shape.centerX - shape.halfSpine) * scale, rt = (shape.centerX + shape.halfSpine) * scale;
        var cy = shape.centerY * scale, rp = r * scale;
        ctx.beginPath();
        ctx.moveTo(l, cy - rp);
        ctx.lineTo(rt, cy - rp);
        ctx.arc(rt, cy, rp, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(l, cy + rp);
        ctx.arc(l, cy, rp, Math.PI / 2, Math.PI * 1.5);
        ctx.closePath();
    }

    var prevRenderTime = 0;
    var gameEventIndex = 0;
    var startPhaseEvent = null;
    var startPhaseTimer = -1;
    var borderDrawnClean = false;
    var floorDrawnClean = false;
    var wallHackShown = 0; // eased shown-depth: grows out fast on grab, tracks the slow drain

    // Is wall-hack active on a worm we care about? Returns {color, frac} (frac = time left,
    // 1 -> 0), preferring the freshest one, or null if not active.
    function wallHackState(renderTime) {
        var worms = gameState.worms;
        var best = null;
        for (var i = 0; i < worms.length; i++) {
            var w = worms[i];
            if (!w.alive) continue;
            var own = !localPlayerIds || localPlayerIds.indexOf(w.playerId) !== -1;
            var effects = csf.getActiveEffects(gameState, renderTime, { wormId: w.id });
            for (var j = 0; j < effects.length; j++) {
                var e = effects[j];
                if (e.name !== "wall_hack") continue;
                if (!own && e.affects !== "all") continue; // someone else's green one is not on your walls
                var frac = 1;
                if (WALLHACK_DURATION > 0 && typeof e.time === "number") {
                    frac = Math.max(0, Math.min(1, (WALLHACK_DURATION - (renderTime - e.time)) / WALLHACK_DURATION));
                }
                if (!best || frac > best.frac) {
                    best = { color: e.affects === "all" ? WALLHACK_ALL_COLOR : WALLHACK_SELF_COLOR, frac: frac };
                }
            }
        }
        return best;
    }

    // Border geometry in PIXELS (the wall-hack designs draw scale-agnostic).
    function wallGeoPx() {
        var shape = gameState.map.shape;
        var bw = gameState.map.borderWidth * scale;
        if (shape.type === "circle") {
            return { type: "circle", cx: shape.centerX * scale, cy: shape.centerY * scale, radius: shape.radius * scale, bw: bw };
        }
        if (shape.type === "stadium") {
            return { type: "stadium", cx: shape.centerX * scale, cy: shape.centerY * scale, hs: shape.halfSpine * scale, radius: shape.radius * scale, bw: bw };
        }
        return { type: "rectangle", x: shape.x * scale, y: shape.y * scale, w: shape.width * scale, h: shape.height * scale, bw: bw };
    }

    function renderBorder(renderTime) {
        var borderWidth = gameState.map.borderWidth;
        var shape = gameState.map.shape;
        if (skinPainter && borderWidth > 0) {
            borderContext.save();
            borderContext.translate(skinOrigin.x, skinOrigin.y);
            skinPainter.paintWall(borderContext, renderTime || 0);
            borderContext.restore();
            return;
        }
        if (shape.type === "rectangle") {
            if (borderWidth > 0) {
                borderContext.fillStyle = boardSkin ? boardSkin.border : MAP_BORDER_COLOR;
                borderContext.beginPath();
                scaledBorderContext.rect(shape.x - borderWidth, shape.y - borderWidth, shape.width + 2*borderWidth, shape.height + 2*borderWidth);
                borderContext.fill();
                scaledBorderContext.clearRect(shape.x, shape.y, shape.width, shape.height);
            }
        } else if (shape.type === "circle") {
            if (borderWidth > 0) {
                borderContext.fillStyle = boardSkin ? boardSkin.border : MAP_BORDER_COLOR;
                borderContext.beginPath();
                scaledBorderContext.arc(shape.centerX, shape.centerY, shape.radius + borderWidth, 0, 2 * Math.PI);
                borderContext.fill();

                borderContext.globalCompositeOperation = "destination-out";
                borderContext.beginPath();
                scaledBorderContext.arc(shape.centerX, shape.centerY, shape.radius, 0, 2 * Math.PI);
                borderContext.fill();
                borderContext.globalCompositeOperation = "source-over";
            }
        } else if (shape.type === "stadium") {
            if (borderWidth > 0) {
                borderContext.fillStyle = boardSkin ? boardSkin.border : MAP_BORDER_COLOR;
                stadiumPath(borderContext, shape, shape.radius + borderWidth);
                borderContext.fill();

                borderContext.globalCompositeOperation = "destination-out";
                stadiumPath(borderContext, shape, shape.radius);
                borderContext.fill();
                borderContext.globalCompositeOperation = "source-over";
            }
        } else {
            throw Error("Unknown shape: " + shape.type);
        }
    }

    function renderBackground(renderTime) {
        var shape = gameState.map.shape;
        if (skinPainter) {
            context.save();
            context.translate(skinOrigin.x, skinOrigin.y);
            skinPainter.paintFloor(context, renderTime || 0);
            context.restore();
            return;
        }
        if (shape.type === "rectangle") {
            context.fillStyle = backgroundColor;
            context.beginPath();
            scaledContext.rect(shape.x, shape.y, shape.width, shape.height);
            context.fill();
        } else if (shape.type === "circle") {
            context.fillStyle = backgroundColor;
            context.beginPath();
            scaledContext.arc(shape.centerX, shape.centerY, shape.radius, 0, 2 * Math.PI);
            context.fill();
        } else if (shape.type === "stadium") {
            context.fillStyle = backgroundColor;
            stadiumPath(context, shape, shape.radius);
            context.fill();
        } else {
            throw Error("Unknown shape: " + shape.type);
        }
    }

    function render(renderTime) {
        if (renderTime < prevRenderTime) {
            gameEventIndex = 0;
            startPhaseEvent = null;
        }
        while (gameEventIndex < gameState.gameEvents.length && gameState.gameEvents[gameEventIndex].time <= renderTime) {
            if (gameState.gameEvents[gameEventIndex].type === "start_phase") {
                startPhaseEvent = gameState.gameEvents[gameEventIndex];
            }
            gameEventIndex++;
        }

        startPhaseTimer = 0;
        if (startPhaseEvent) {
            startPhaseTimer = Math.ceil(Math.max(0, startPhaseEvent.time + startPhaseEvent.duration - renderTime));
        }
        // A skinned floor is a full-canvas drawImage blit of a cached bitmap (or, for the
        // flat-colour default, a cheap fill) - only re-blit it once per skin/geometry unless
        // the skin animates its floor (e.g. dungeon's torchlight), mirroring borderDrawnClean
        // below. Nothing else ever draws on this canvas, so a stale blit stays correct.
        var liveFloor = skinPainter && skinPainter.animated;
        if (liveFloor || !floorDrawnClean) {
            renderBackground(renderTime);
            floorDrawnClean = true;
        }

        // Wall-hack: animate the border while active (redraw the clean border + effect each
        // frame); otherwise draw the plain border once. Suppresses the old head ring for it.
        var wh = wallHackState(renderTime);
        // Ease the shown depth toward the true remaining fraction: a jump up (grab or
        // re-grab) grows the band out quickly from wherever it is; the slow decline of the
        // fraction is tracked as the drain. Time-based so it's frame-rate independent.
        var target = wh ? wh.frac : 0;
        var dt = renderTime - prevRenderTime;
        if (dt < 0) dt = 0; // render-clock rewind (replay scrub): don't ease this frame
        wallHackShown += (target - wallHackShown) * Math.min(1, dt / 0.11);
        // An animated skin wall (torch flames, the gold sheen) also redraws every frame.
        var liveWall = skinPainter && skinPainter.animated;
        if (wh) {
            borderContext.clearRect(0, 0, borderCanvas.width, borderCanvas.height);
            renderBorder(renderTime);
            drawWallHackWalls(borderContext, wallGeoPx(), getWallHackVariant(), renderTime, wh.color, wallHackShown);
            borderDrawnClean = false;
        } else if (liveWall || !borderDrawnClean) {
            borderContext.clearRect(0, 0, borderCanvas.width, borderCanvas.height);
            renderBorder(renderTime);
            borderDrawnClean = true;
        }

        prevRenderTime = renderTime;
    };

    return {
        render: render
    };
};
